import {
  FEATURE_IDS,
  PLATFORMS_WITH_LOCALSTORAGE,
  type FeatureId,
} from 'src/tpbp-core/feature-flag/featureFlags.const'

const featureFlagContext = {
  platform: '',
  canUseLocalStorage: false,
  configurationFeatureFlags: {} as Record<string, boolean>,
}

export function setPlatform(platform: string) {
  featureFlagContext.platform = platform
  if (PLATFORMS_WITH_LOCALSTORAGE.includes(platform)) {
    featureFlagContext.canUseLocalStorage = true
  }
}

export function getCanOverrideFeatureFlags() {
  return featureFlagContext.canUseLocalStorage
}

export function setConfigurationFeatureFlag(
  featureId: string,
  value: boolean | string,
) {
  featureFlagContext.configurationFeatureFlags[featureId] =
    typeof value === 'boolean' ? value : value === 'true'
}

export function getFeatureFlags() {
  return Object.keys(FEATURE_IDS).filter((featureId) =>
    hasFeature(featureId as FeatureId),
  )
}

export function hasFeature(featureId: FeatureId) {
  const configurationFeatureFlagValue = getConfigurationFeatureFlag(featureId)
  const localStorageFeatureFlagValue = getLocalStorageFeatureFlag(featureId)

  if (
    featureFlagContext.canUseLocalStorage &&
    localStorageFeatureFlagValue !== undefined
  )
    return localStorageFeatureFlagValue // localStorage overrides configuration

  return configurationFeatureFlagValue === true
}

export type HasFeatureFunction = typeof hasFeature

export function setLocalStorageFeatureFlag(
  featureId: string,
  hasFeature: boolean,
) {
  if (!featureFlagContext.canUseLocalStorage)
    throw new Error(
      'Can not use localStorage for featureFlags with this platform',
    )

  localStorage.setItem(featureId, hasFeature ? 'true' : 'false')
}

export function removeLocalStorageFeatureFlag(featureId: string) {
  if (!featureFlagContext.canUseLocalStorage)
    throw new Error(
      `Can not use localStorage for featureFlags with this platform '${featureFlagContext.platform}'`,
    )

  localStorage.removeItem(featureId)
}

export function getCurrentPlatform() {
  return featureFlagContext.platform
}

export function getConfigurationFeatureFlag(
  featureId: string,
): boolean | undefined {
  return featureFlagContext.configurationFeatureFlags[featureId]
}

export function getLocalStorageFeatureFlag(
  featureId: string,
): boolean | undefined {
  if (!featureFlagContext.canUseLocalStorage) return

  const value = localStorage.getItem(featureId)

  if (typeof value !== 'string') return

  return value === 'true'
}
